import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AccountLayout } from '../components/account/AccountLayout';
import { LoginSecuritySection } from '../components/account/LoginSecuritySection';
import { AddressesSection } from '../components/account/AddressesSection';
import { OrdersSection } from '../components/account/OrdersSection';
import { WalletSection } from '../components/account/WalletSection';
import { LoyaltyPointsSection } from '../components/account/LoyaltyPointsSection';
import { WishlistSection } from '../components/account/WishlistSection';
import { ReturnsSection } from '../components/account/ReturnsSection';
import { CouponsSection } from '../components/account/CouponsSection';
import { PaymentRequestsSection } from '../components/account/PaymentRequestsSection';
import { PaymentRequestDetailsSection } from '../components/account/PaymentRequestDetailsSection';
import { SEO } from '../components/shared/SEO';
import { UserProfileCard } from '../components/account/UserProfileCard';
import { QuickActionsGrid } from '../components/account/QuickActionsGrid';
import { LoyaltyCard } from '../components/account/LoyaltyCard';
import { ReferralCard } from '../components/account/ReferralCard';
import { AccountMenuList } from '../components/account/AccountMenuList';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { AuthBottomSheet } from '../components/modals/AuthBottomSheet';
import { LoyaltyOnboardingBottomSheet } from '../components/modals/LoyaltyOnboardingBottomSheet';
import { EditProfileBottomSheet } from '../components/account/EditProfileBottomSheet';
import { ChangePasswordBottomSheet } from '../components/account/ChangePasswordBottomSheet';
import { WalletBottomSheet } from '../components/account/WalletBottomSheet';
import { LoyaltyPointsBottomSheet } from '../components/account/LoyaltyPointsBottomSheet';
import { ArrowLeftIcon } from 'lucide-react';
import { LanguageBottomSheet } from '../components/modals/LanguageBottomSheet';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { useStore } from '../lib/store';
export function AccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    language,
    setLanguage
  } = useStore();
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  const [editProfileBottomSheetOpen, setEditProfileBottomSheetOpen] = useState(false);
  const [changePasswordBottomSheetOpen, setChangePasswordBottomSheetOpen] = useState(false);
  const [walletBottomSheetOpen, setWalletBottomSheetOpen] = useState(false);
  const [pointsBottomSheetOpen, setPointsBottomSheetOpen] = useState(false);
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const user = {
    name: 'Ahmed Mohammed',
    email: 'ahmed.mohammed@example.com',
    phone: '+966 50 123 4567',
    avatar: ''
  };
  const userProfileData = {
    name: 'Ahmed Mohammed',
    email: 'ahmed.mohammed@example.com',
    phone: '+966 50 123 4567',
    gender: 'Male',
    maritalStatus: 'Single',
    nationality: 'Saudi Arabian',
    dateOfBirth: '1990-05-15',
    workingSector: 'Technology',
    city: 'Riyadh',
    district: 'Al Olaya',
    preferredLanguage: 'English'
  };
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/addresses')) return 'addresses';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/wallet')) return 'wallet';
    if (path.includes('/points')) return 'points';
    if (path.includes('/wishlist')) return 'wishlist';
    if (path.includes('/returns')) return 'returns';
    if (path.includes('/coupons')) return 'coupons';
    if (path.includes('/payment-requests')) return 'payment-requests';
    return 'login-security';
  };
  const [activeSection, setActiveSection] = useState(getActiveSection());
  useEffect(() => {
    const newSection = getActiveSection();
    if (newSection !== activeSection) {
      setActiveSection(newSection);
    }
    if (window.innerWidth < 768) {
      if (newSection === 'wallet') {
        setWalletBottomSheetOpen(true);
      } else if (newSection === 'points') {
        setPointsBottomSheetOpen(true);
      }
    }
  }, [location]);
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      // If profile is complete, set loyalty member data
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const getSEOData = () => {
    switch (activeSection) {
      case 'orders':
        return {
          title: 'My Orders',
          description: 'View order status, invoices, and tracking.',
          canonical: '/en/account/orders/'
        };
      case 'wishlist':
        return {
          title: 'Wishlist',
          description: 'Save products you love.',
          canonical: '/en/wishlist/'
        };
      case 'addresses':
        return {
          title: 'My Addresses',
          description: 'Manage your delivery addresses.',
          canonical: '/en/account/addresses/'
        };
      case 'wallet':
        return {
          title: 'My Wallet',
          description: 'View your wallet balance.',
          canonical: '/en/account/wallet/'
        };
      case 'points':
        return {
          title: 'Loyalty Points',
          description: 'Track and redeem your loyalty points.',
          canonical: '/en/account/points/'
        };
      case 'returns':
        return {
          title: 'Returns',
          description: 'Manage your product returns.',
          canonical: '/en/account/returns/'
        };
      case 'coupons':
        return {
          title: 'My Coupons',
          description: 'View and manage your coupons.',
          canonical: '/en/account/coupons/'
        };
      case 'payment-requests':
        return {
          title: 'Payment Requests',
          description: 'View and manage your payment links and pending payments.',
          canonical: '/en/account/payment-requests/'
        };
      default:
        return {
          title: 'My Account',
          description: 'Manage your account.',
          canonical: '/en/account/'
        };
    }
  };
  const seoData = getSEOData();
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    const routes = {
      'login-security': '/account',
      addresses: '/account/addresses',
      orders: '/account/orders',
      wallet: '/account/wallet',
      points: '/account/points',
      wishlist: '/account/wishlist',
      returns: '/account/returns',
      coupons: '/account/coupons',
      'payment-requests': '/account/payment-requests'
    };
    navigate(routes[section] || '/account');
  };
  const renderSection = () => {
    // Check if we're on a payment request details page
    if (location.pathname.includes('/payment-requests/') && location.pathname.split('/').length > 3) {
      return <PaymentRequestDetailsSection data-id="element-5971" data-mp="1y7slec7jpnzs" />;
    }
    switch (activeSection) {
      case 'login-security':
        return <LoginSecuritySection data-id="element-5972" data-mp="1t74tzpfen551" />;
      case 'addresses':
        return <AddressesSection data-id="element-5973" data-mp="20rp8pmx08jnb" />;
      case 'orders':
        return <OrdersSection data-id="element-5974" data-mp="25rtpzf5qliel" />;
      case 'wallet':
        return <WalletSection data-id="element-5975" data-mp="25qijh7kwpwjs" />;
      case 'points':
        return <LoyaltyPointsSection data-id="element-5976" data-mp="2aitlcfz7jsez" />;
      case 'wishlist':
        return <WishlistSection data-id="element-5977" data-mp="2ajbpdys43axg" />;
      case 'returns':
        return <ReturnsSection data-id="element-5978" data-mp="2fhwfh5dnge86" />;
      case 'coupons':
        return <CouponsSection data-id="element-5979" data-mp="2fjplqjkcowfn" />;
      case 'payment-requests':
        return <PaymentRequestsSection data-id="element-5980" data-mp="1hv64nc125lxm" />;
      default:
        return <LoginSecuritySection data-id="element-5981" data-mp="1msz6jsvbvts3" />;
    }
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    // Update loyalty data to enrolled state
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const handleWalletClose = () => {
    setWalletBottomSheetOpen(false);
    navigate('/account');
  };
  const handlePointsClose = () => {
    setPointsBottomSheetOpen(false);
    navigate('/account');
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
    setLanguageBottomSheetOpen(false);
  };
  const isOnSectionPage = activeSection !== 'login-security';
  return <HelmetProvider data-id="element-5982" data-mp="358nz0flgr7ff">
      <SEO title={seoData.title} description={seoData.description} canonical={seoData.canonical} robots="noindex,follow" data-id="element-5983" data-mp="359xwp3oemdlj" />
      <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden" data-id="element-5984" data-mp="3f8516n1ib36n">
        <div className="hidden md:block" data-id="element-5985" data-mp="pia1bcp1gt4u">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={false} data-id="element-5986" data-mp="phi5c0rj4te1" />
        </div>
        <main className="flex-grow overflow-x-hidden pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-5987" data-mp="2a02g1ldpwynj">
          <h1 className="sr-only" data-id="element-5988" data-mp="258u4ojstv37q">My Account Dashboard</h1>
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-5989" data-mp="2579baxi91qbg">
            <MobileInternalHeader title={isOnSectionPage ? seoData.title : 'My Account'} data-id="element-5990" data-mp="209zlvhuv1enm" />
          </div>
          {/* Mobile Content */}
          <div className="md:hidden" data-id="element-5991" data-mp="1r9l06hdzhxjl">
            {!isOnSectionPage ? <PullToRefresh onRefresh={async () => {
            await new Promise(r => setTimeout(r, 600));
          }} data-id="element-5992" data-mp="1mahv4wy07rbm">
                <UserProfileCard user={user} onSignIn={() => setAuthBottomSheetOpen(true)} onRegister={() => setAuthBottomSheetOpen(true)} onEdit={() => setEditProfileBottomSheetOpen(true)} stats={{
              points: loyaltyData.pointsBalance ?? 3250,
              walletBalance: 'SAR 250',
              orders: 5
            }} tier={loyaltyData.tier ? `${loyaltyData.tier.charAt(0).toUpperCase()}${loyaltyData.tier.slice(1)} Member` : 'Gold Member'} data-id="element-5993" data-mp="1hdrg8cxxr1rs" />
                <QuickActionsGrid data-id="element-5994" data-mp="1srt3dwjofieh" />
                <div className="px-4 mt-4" data-id="element-5995" data-mp="1xpunmksu9nrj">
                  <LoyaltyCard progress={loyaltyProgress} onJoinNow={() => setLoyaltyBottomSheetOpen(true)} onViewDetails={() => setPointsBottomSheetOpen(true)} loyaltyData={loyaltyData} data-id="element-5996" data-mp="1xos08muq63gd" />
                </div>
                <div className="px-4 mt-4" data-id="element-5997" data-mp="89limzhhuwya">
                  <ReferralCard data-id="element-5998" data-mp="cci551a5nd7i" />
                </div>
                <div className="px-4 mt-4" data-id="element-5999" data-mp="ceb9ub17jncv">
                  <button onClick={() => setChangePasswordBottomSheetOpen(true)} className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center justify-between active:scale-[0.99] hover:border-[#1E2B55] transition" data-id="element-6000" data-mp="4sz4lh4vb1b6">
                    <div className="flex items-center gap-3" data-id="element-6001" data-mp="9rsivbrzawce">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center" data-id="element-6002" data-mp="9q8ypa37c15c">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-6003" data-mp="3i5sq03kbb9hr">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" data-id="element-6004" data-mp="3n66zlq1karhw" />
                        </svg>
                      </div>
                      <div className="text-left" data-id="element-6005" data-mp="21rzqxj5szexp">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-6006" data-mp="21qohb2pofcyo">
                          Change Password
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-6007" data-mp="2biu8wbc0nsdc">
                          Update your password
                        </p>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-6008" data-mp="2lg8pcaxxwh5n">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-6009" data-mp="2ug5a41h7kvvf" />
                    </svg>
                  </button>
                </div>
                <div className="px-4 mt-4" data-id="element-6010" data-mp="skoav9b8n6hg">
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-6011" data-mp="slpp1mnjw93e">
                    <AccountMenuList onLanguageClick={() => setLanguageBottomSheetOpen(true)} data-id="element-6012" data-mp="iu34gzpboxnz" />
                  </div>
                </div>
                <div className="px-4 py-4 bg-white border-t border-gray-200 mt-4" data-id="element-6013" data-mp="dsxbd0rr38dx">
                  <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-6014" data-mp="1bhz8kph81rus">
                    Follow Us
                  </h3>
                  <div className="flex items-center justify-around gap-3" data-id="element-6015" data-mp="16hunsn9a6ayr">
                    <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-6016" data-mp="11irm9csiemvj">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6017" data-mp="3i8yorifgj5x0">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-6018" data-mp="3joash4tnlu1c">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-6019" data-mp="39uk8od8h27rz" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-6020" data-mp="2r2rewglkwij7">TikTok</span>
                    </a>
                    <a href="https://www.snapchat.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-6021" data-mp="2w1ul44daousr">
                      <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6022" data-mp="23ebfjqjgwsqv">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-6023" data-mp="1ygjjqlfpcmhe">
                          <path d="M12.206 2.024c-1.013 0-2.022.196-2.982.579a7.532 7.532 0 0 0-2.531 1.672 7.91 7.91 0 0 0-1.713 2.482c-.408.938-.615 1.942-.615 2.982 0 1.228.267 2.403.793 3.493.526 1.09 1.302 2.046 2.305 2.839.265.21.497.446.69.702a3.43 3.43 0 0 1 .446.87c.055.146.089.298.1.452.011.154-.011.308-.066.452l-.497 1.304c-.044.11-.055.232-.033.348.022.116.077.224.16.308.083.084.19.139.308.16.116.022.238.011.348-.033l1.793-.662c.232-.088.485-.11.728-.066.243.044.475.143.675.286.43.308.905.55 1.412.719a6.38 6.38 0 0 0 1.625.254c1.013 0 2.022-.196 2.982-.579a7.532 7.532 0 0 0 2.531-1.672 7.91 7.91 0 0 0 1.713-2.482c.408-.938.615-1.942.615-2.982a7.532 7.532 0 0 0-.615-2.982 7.91 7.91 0 0 0-1.713-2.482 7.532 7.532 0 0 0-2.531-1.672 7.532 7.532 0 0 0-2.982-.579z" data-id="element-6024" data-mp="27ed736vho5sf" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-6025" data-mp="kcbr367c2rfl">Snapchat</span>
                    </a>
                    <a href="https://www.x.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-6026" data-mp="pbek1sa1h777">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6027" data-mp="3sswqpwe2s861">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-6028" data-mp="6cag2xxcu3me">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-6029" data-mp="3a1dohrrvky52" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-6030" data-mp="2xjkxubfq0btz">X</span>
                    </a>
                    <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-6031" data-mp="2sidkzxyq0mgg">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6032" data-mp="2es0d1vltx7r4">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-6033" data-mp="24uvzul9yommg">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-6034" data-mp="1vtfpuen26mir" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-6035" data-mp="1priouu7h0xe5">Instagram</span>
                    </a>
                    <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-6036" data-mp="1znc78u7vfl3u">
                      <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6037" data-mp="1707xkbxu2ilq">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-6038" data-mp="1fzl9db9fjj9m">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-6039" data-mp="o8f6wo68l9h6" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-6040" data-mp="25ahkjx6syhqy">Facebook</span>
                    </a>
                    <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 group" data-id="element-6041" data-mp="20bedk779i1gc">
                      <div className="w-12 h-12 rounded-full bg-blue-700 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-6042" data-mp="17oa3vox84yy8">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" data-id="element-6043" data-mp="1hl502160yn9c">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-6044" data-mp="2t0lc366pur1l" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600" data-id="element-6045" data-mp="3o8vxsxr6el17">LinkedIn</span>
                    </a>
                  </div>
                </div>
              </PullToRefresh> : <>
                <div className="sticky top-[56px] z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 shadow-sm" data-id="element-6046" data-mp="3eiu7s0f9isjn">
                  <button onClick={() => navigate('/account')} aria-label="Back to account" className="p-2 -ml-2 active:scale-95 hover:bg-gray-100 rounded-full transition" data-id="element-6047" data-mp="61zijnre16o1">
                    <ArrowLeftIcon className="w-5 h-5 text-gray-700" data-id="element-6048" data-mp="2ecid1ont1vsm" />
                  </button>
                  <h2 className="text-base font-bold text-gray-900" data-id="element-6049" data-mp="29cvztf7z8fjt">
                    {activeSection === 'orders' && 'My Orders'}
                    {activeSection === 'addresses' && 'My Addresses'}
                    {activeSection === 'wishlist' && 'My Wishlist'}
                    {activeSection === 'returns' && 'Returns'}
                    {activeSection === 'coupons' && 'My Coupons'}
                    {activeSection === 'payment-requests' && 'Payment Requests'}
                  </h2>
                </div>
                <div className="p-4" data-id="element-6050" data-mp="2nbvq0rb512i3">{renderSection()}</div>
              </>}
          </div>
          {/* Desktop Content */}
          <div className="hidden md:block" data-id="element-6051" data-mp="3kudwxvv1ygxd">
            <AccountLayout activeSection={activeSection} onSectionChange={handleSectionChange} data-id="element-6052" data-mp="3g12m7jn4dg06">
              {renderSection()}
            </AccountLayout>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-6053" data-mp="3scli0ixj7wmr">
          <Footer data-id="element-6054" data-mp="3sdn4rknwu7kb" />
        </div>
        <MobileFooterNav data-id="element-6055" data-mp="159bqyy6t3lm" />
        <PharmacistChat data-id="element-6056" data-mp="14r3f3y92yxc" />
        <div className="h-16 md:hidden" data-id="element-6057" data-mp="17ls57oar5wk"></div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6058" data-mp="64voz8e0mjuf" />
        <AuthBottomSheet isOpen={authBottomSheetOpen} onClose={() => setAuthBottomSheetOpen(false)} onAuthSuccess={handleAuthSuccess} data-id="element-6059" data-mp="f2xm3cumoscf" />
        <LoyaltyOnboardingBottomSheet isOpen={loyaltyBottomSheetOpen} onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-6060" data-mp="oubq9xxiygbw" />
        <EditProfileBottomSheet isOpen={editProfileBottomSheetOpen} onClose={() => setEditProfileBottomSheetOpen(false)} userInfo={userProfileData} onSave={data => console.log('Saving profile:', data)} data-id="element-6061" data-mp="ytssg5dkic34" />
        <ChangePasswordBottomSheet isOpen={changePasswordBottomSheetOpen} onClose={() => setChangePasswordBottomSheetOpen(false)} onSubmit={data => console.log('Changing password:', data)} data-id="element-6062" data-mp="ivgrmhqvbqxt" />
        <WalletBottomSheet isOpen={walletBottomSheetOpen} onClose={handleWalletClose} data-id="element-6063" data-mp="rubvhn6fwoxy" />
        <LoyaltyPointsBottomSheet isOpen={pointsBottomSheetOpen} onClose={handlePointsClose} data-id="element-6064" data-mp="mwaed7skqzp4" />
        <LanguageBottomSheet isOpen={languageBottomSheetOpen} onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-6065" data-mp="11rgkrvvl10iz" />
      </div>
    </HelmetProvider>;
}